'use strict';

/**
 * default conversion type
 */
var DEFAULT_CONVERSION_TYPE = 'dec';
/**
 * Bindings for custom decimal separator and
 * conversion type. Set by init().
 */
var _decimalSeparator = '';
var _conversionType = DEFAULT_CONVERSION_TYPE;
/**
 * default decimal separator
 */
var DEFAULT_DECIMAL_SEPARATOR = '.';
var formatter = new Intl.NumberFormat(window.navigator.language, {
  useGrouping: false,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
/**
 * fractional mapping with sportradar standard
 * from decimal to fractional and vice versa
 */
var _fractionalMapping = {
  decValues: ['1.01', '1.02', '1.03', '1.04', '1.05', '1.06', '1.07', '1.08', '1.09', '1.10', '1.11', '1.12', '1.13', '1.14', '1.15', '1.17', '1.18', '1.20', '1.22', '1.25', '1.29', '1.30', '1.33', '1.35', '1.36', '1.40', '1.44', '1.45', '1.47', '1.50', '1.53', '1.57', '1.60', '1.62', '1.63', '1.67', '1.70', '1.73', '1.80', '1.83', '1.90', '1.91', '1.95', '2.00', '2.05', '2.10', '2.20', '2.25', '2.30', '2.38', '2.40', '2.50', '2.63', '2.70', '2.75', '2.80', '2.88', '2.90', '3.00', '3.10', '3.13', '3.20', '3.25', '3.30', '3.38', '3.40', '3.50', '3.60', '3.75', '3.80', '4.00', '4.20', '4.33', '4.50', '4.60'],
  fractionalValues: ['1/100', '1/50', '1/33', '1/25', '1/20', '1/16', '1/14', '1/12', '1/11', '1/10', '1/9', '2/17', '2/15', '1/7', '2/13', '1/6', '2/11', '1/5', '2/9', '1/4', '2/7', '3/10', '1/3', '7/20', '4/11', '2/5', '4/9', '9/20', '8/17', '1/2', '8/15', '4/7', '3/5', '8/13', '5/8', '4/6', '7/10', '8/11', '4/5', '5/6', '9/10', '10/11', '20/21', '1/1' /*+" (EVS)"*/, '21/20', '11/10', '6/5', '5/4', '13/10', '11/8', '7/5', '3/2', '13/8', '17/10', '7/4', '9/5', '15/8', '19/10', '2/1', '21/10', '17/8', '11/5', '9/4', '23/10', '19/8', '12/5', '5/2', '13/5', '11/4', '14/5', '3/1', '16/5', '10/3', '7/2', '18/5']
};
// Converter functions
var converterFunctions = {
  /**
   * @param (float) $odds in 'decimal' format
   * @return string number formatted $odds with 2 digits (e.g. 11.72)
   */
  _dec2dec: function _dec2dec() {
    var replacerFunction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _formatOdds;
    return function (odds) {
      var shortOdd = (Math.round(odds * 100) / 100).toFixed(2);
      return replacerFunction(shortOdd);
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into 'US ( money line )' format (e.g. +1073)
   */
  _dec2us: function _dec2us() {
    return function (odds) {
      return odds < 2 && odds > 1 ? (-100 / (odds - 1)).toFixed(0) : '+' + (100 * (odds - 1)).toFixed(0);
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into 'Hong Kong' format (e.g. 10.72)
   */
  _dec2hk: function _dec2hk() {
    var replacerFunction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _formatOdds;
    return function (odds) {
      return replacerFunction((odds - 1).toFixed(2));
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into 'indonesian' format (e.g. 10.72)
   */
  _dec2indo: function _dec2indo() {
    var replacerFunction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _formatOdds;
    return function (odds) {
      var hkOdds = odds - 1;
      return replacerFunction(hkOdds < 1 && hkOdds > 0 ? (-1 / hkOdds).toFixed(2) : hkOdds.toFixed(2));
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into 'malaysian' format (e.g. -0.09)
   */
  _dec2my: function _dec2my() {
    var replacerFunction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _formatOdds;
    return function (odds) {
      var hkOdds = odds - 1;
      return replacerFunction(hkOdds <= 1 ? hkOdds.toFixed(2) : (-1 / hkOdds).toFixed(2));
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into 'probability' format (e.g. 0.09)
   */
  _dec2prob: function _dec2prob() {
    var replacerFunction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _formatOdds;
    return function (odds) {
      odds = 1 / odds;
      return replacerFunction(odds.toFixed(2));
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into fractional format (e.g. 268/25)
   */
  _dec2frac: function _dec2frac() {
    return function (odds) {
      var mapping = _mapFractional(odds);
      if (mapping === false) {
        odds = Number(odds.toFixed(2));
        var factor = 1;
        while (Math.abs(odds - Math.round(odds)) > 0.000001) {
          odds = odds * 10;
          factor = factor * 10;
        }
        odds = Math.round(odds);
        var ggT = _getBiggestDivisor(odds, factor);
        return (odds - factor) / ggT + '/' + factor / ggT;
      } else {
        return mapping;
      }
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into 'lay' odds. Lay odds are ceil to two decimals (e.g. '5.1111' => 5.12)
   */
  _dec2lay: function _dec2lay() {
    var replacerFunction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _formatOdds;
    return function (odds) {
      var layOdds = 1 / (1 - 1 / odds);
      layOdds = Math.round(layOdds * 1000000000000) / 1000000000000;
      return replacerFunction((Math.ceil(layOdds * 100) / 100).toFixed(2));
    };
  },
  /**
   * @param (float) $odds in 'decimal' format
   * @return string $odds converted into 'percnostake' odds. (e.g. '1.35' => 35)
   */
  _dec2india: function _dec2india() {
    return function (odds) {
      return ((odds - 1) * 100).toFixed(0);
    };
  }
};
/**
 * fractional mapping for a decimal value
 * @param decmapping
 * @return {*} return fractional mapping value or false if no mapping is available
 */
function _mapFractional(decmapping) {
  _pad(parseFloat(decmapping.toFixed(2)), 2);
  var index = _fractionalMapping.decValues.indexOf(decmapping.toString());
  if (index >= 0) {
    return _fractionalMapping.fractionalValues[index];
  } else {
    return false;
  }
}
/**
 * get biggest divisor for two numbers
 * @param a number one
 * @param b number two
 * @return returns the biggest divisor of both passed numbers
 * @private
 */
function _getBiggestDivisor(a, b) {
  if (b == 0) {
    return a;
  } else {
    return _getBiggestDivisor(b, a % b);
  }
}
/**
 *
 * @param num number to format
 * @param size how many characters behind the decimal separator
 * @return {string} formatted string with fixed numbers of decimal values
 * @private
 */
function _pad(num, size) {
  return parseFloat(num.toFixed(size)).toString();
}
/**
 * replace the decimal separator, which is given by the default number (.)
 * and replace it with the decimal separator from the feature
 * @param formattedRate odd/rate to reformat with another decimal separator
 * @return {string} number as string with replaced decimal separator
 */
function _formatOdds(formattedRate) {
  if (_conversionType === 'dec' && _decimalSeparator === 'browserLocale') {
    return formatter.format(parseFloat(formattedRate));
  }
  return !_decimalSeparator ? formattedRate.toString() : formattedRate.toString().replace(DEFAULT_DECIMAL_SEPARATOR, _decimalSeparator);
}
/**
 * set the body class with the oddstype; class name ot-* (ot-[oddsTypeAbbrevation])
 * @return {void}
 */
function _setBodyClass(conversionType) {
  var bodyClass = "ot-".concat(conversionType);
  if (typeof conversionType !== 'undefined' && conversionType !== null && document && document.body && !document.body.classList.contains(bodyClass)) {
    document.body.classList.add(bodyClass);
  }
}
/**
 * @return {string} Return current conversion type
 */
function getConversionType() {
  return _conversionType;
}
/**
 * @param (string|integer|double) $odds in 'decimal' format will be parsed to float
 * @param string $type accepts 'dec' | 'us' | 'hk' | 'indo' | 'my' | 'prob' | 'frac' | 'lay' | 'india' | undefined | null;
 * @return mixed|string odds in specified format
 *
 */
function convertOdds(odds, type) {
  type = type || _conversionType;
  odds = Number(odds);
  var conversionFunction = typeof converterFunctions["_dec2".concat(type)] === 'function' ? converterFunctions["_dec2".concat(type)] : converterFunctions['_dec2dec'];
  return conversionFunction()(odds);
}
/**
 * @param {string} conversionType defines current conversion type (default: dec)
 * @param {string} decimalSeparator defines decimal sign (default: .)
 * Invokes setBodyClass to set class on document.body
 * @return {object} Returns convertOdds function and getConversionType getter
 */
function init() {
  var conversionType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : window.srvg && window.srvg.oddsType || DEFAULT_CONVERSION_TYPE;
  var decimalSeparator = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : DEFAULT_DECIMAL_SEPARATOR;
  _conversionType = conversionType;
  _decimalSeparator = decimalSeparator;
  _setBodyClass(conversionType);
  return {
    convertOdds: convertOdds,
    getConversionType: getConversionType
  };
}
/**
 * sets the default conversionType and default decimalSeparator
 * @return {object} Returns convertOdds function and getConversionType getter
 */
function basicInit() {
  _conversionType = window.srvg && window.srvg.oddsType || DEFAULT_CONVERSION_TYPE;
  _decimalSeparator = DEFAULT_DECIMAL_SEPARATOR;
  return {
    convertOdds: convertOdds,
    getConversionType: getConversionType
  };
}
/**
 * Pull out curried functions from converterFunctions
 * object for single named exports.
 */
var dec2dec = converterFunctions._dec2dec;
var dec2us = converterFunctions._dec2us;
var dec2hk = converterFunctions._dec2hk;
var dec2indo = converterFunctions._dec2indo;
var dec2my = converterFunctions._dec2my;
var dec2prob = converterFunctions._dec2prob;
var dec2frac = converterFunctions._dec2frac;
var dec2lay = converterFunctions._dec2lay;
var dec2india = converterFunctions._dec2india;

exports.basicInit = basicInit;
exports.dec2dec = dec2dec;
exports.dec2frac = dec2frac;
exports.dec2hk = dec2hk;
exports.dec2india = dec2india;
exports.dec2indo = dec2indo;
exports.dec2lay = dec2lay;
exports.dec2my = dec2my;
exports.dec2prob = dec2prob;
exports.dec2us = dec2us;
exports.init = init;
